import { useCallback, useEffect, useMemo, useState } from "react";
import { IGif } from "@/common/api/services/types";
import { useFeedsStore } from "@/common/store/feedsStore";
import { useGifStore } from "@/common/store/gifStore";
import { useUsersStore } from "@/common/store/storeUsers";
import { AxiosError } from "axios";
import { apiGifs } from "@/common/services";

export const useGalleryFeed = (galleryId: string) => {
  const key = ["gallery", galleryId].join(":");

  const feed = useFeedsStore((state) => state.getFeed(key));
  const appendFeed = useFeedsStore((state) => state.appendFeed);
  const updateGifs = useGifStore((state) => state.updateGifs);
  const getGifById = useGifStore((state) => state.getGifById);
  const gifsData = useGifStore((state) => state.gifs);
  const updateUsers = useUsersStore((state) => state.updateUsers);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<AxiosError | null>(null);

  const { ids = [] } = feed || {};

  const fetchRequest = useCallback(async () => {
    try {
      setLoading(true);

      const res = await apiGifs.getGallery(galleryId);

      if (res) {
        updateGifs(res.gifs);
        updateUsers(Object.values(res.users));

        appendFeed(key, {
          ids: res.gifs.map(gif => gif.id),
        });
      }
    } catch (e) {
      const error = e as AxiosError;
      setError(error);
      console.error("Error reading gifs.", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (feed === undefined) {
      fetchRequest();
    }
  }, [fetchRequest, feed]);

  const gifs = useMemo(() => {
    const gifs: IGif[] = [];

    ids.forEach(id => {
      const gif = getGifById(id);

      if (gif) {
        gifs.push(gif);
      }
    });

    return gifs;
  }, [ids, getGifById, gifsData]);

  return {
    gifs,
    loading,
    error,
  };
};
